import { useState, useCallback } from 'react';
import { getTextCache } from './useJoinFormData';

/**
 * Custom hook for managing form validation logic in the join form workflow.
 *
 * This hook encapsulates validation-related concerns including password strength
 * management, form error handling, and error message resolution with localized text.
 *
 * @param {Object} params - Hook configuration parameters
 * @param {Function} params.setError - react-hook-form function to set field errors
 * @param {Function} params.clearErrors - react-hook-form function to clear all errors
 * @param {Function} params.onFailedSubmission - Callback executed when form submission fails
 * @param {Function} params.trackInvalidJoin - Function to track invalid submissions for analytics
 *
 * @returns {Object} Hook interface
 * @returns {string|null} returns.passwordStrength - Current password strength ('strong'|'good'|'weak'|null)
 * @returns {Function} returns.handlePasswordStrengthChange - Handler for password strength changes
 * @returns {Function} returns.setInitialFormError - Sets initial form errors with localized messages
 * @returns {Function} returns.handleInvalidSubmission - Handles invalid form submissions with analytics
 * @returns {Function} returns.handleFormError - Handles general form errors (e.g., API errors)
 */
export const useJoinFormValidation = ({
  setError,
  clearErrors,
  onFailedSubmission,
  trackInvalidJoin,
}) => {
  // Password strength state management
  const [passwordStrength, setPasswordStrength] = useState(null);

  const handlePasswordStrengthChange = useCallback((strength) => {
    const strengthMap = {
      STRONG: 'strong',
      GOOD: 'good',
      WEAK: 'weak',
      NOT_MET: '',
    };

    const newPasswordStrength = strengthMap[strength];
    if (newPasswordStrength) {
      setPasswordStrength(newPasswordStrength);
    }
  }, []);

  const setInitialFormError = useCallback((errors = {}) => {
    const textCache = getTextCache();

    if (Object.hasOwnProperty.call(errors, 'password') && errors.password.type === 'validate') {
      setError('password', { message: textCache?.createPasswordRulesText });
    }

    setError('form', { message: textCache?.joinForm?.error });
  }, [setError]);

  const handleInvalidSubmission = useCallback((errors) => {
    setInitialFormError(errors);

    // Track invalid phone number specifically
    if (errors.phone?.type === 'required' || errors.phone?.type === 'validate') {
      trackInvalidJoin('INVALID_PHONE_NUMBER');
    }
  }, [setInitialFormError, trackInvalidJoin]);

  const handleFormError = useCallback((createUserResponse) => {
    const { errors = [] } = createUserResponse;
    const error = errors.find((err) => err?.message) || {};
    const { message, status } = error;

    if (!message) return;

    const { key: errorKey } = message;
    trackInvalidJoin(errorKey);

    setError('form', { message, status });
    onFailedSubmission(createUserResponse);
  }, [setError, onFailedSubmission, trackInvalidJoin]);

  const clearAllErrors = useCallback(() => {
    clearErrors();
  }, [clearErrors]);

  return {
    // Password strength management
    passwordStrength,
    handlePasswordStrengthChange,

    // Error handling functions
    setInitialFormError,
    handleInvalidSubmission,
    handleFormError,
    clearAllErrors,
  };
};
